import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { RETURNS_INTAKE_STATUS_LABELS } from '../config/statusChip';
import { ReturnsIntakeFilters } from '../models';
import { useState, useEffect } from 'react';
import {
  Search as SearchIcon,
  DeleteForever as TrashIcon,
} from '@mui/icons-material';
import {
  GlobalButton,
  AutocompleteInput,
  SelectInput,
  DateInput,
  TextInput,
} from '@/components';
import {
  useDriverSearchInput,
  useCommerceSearchInput,
  useDepartmentSearchInput,
} from '../hooks';

interface SearchBarProps {
  search: (filters: ReturnsIntakeFilters) => void;
  initialFilter: ReturnsIntakeFilters;
  filter: ReturnsIntakeFilters;
  hideStatus?: boolean;
  dateLabel?: string;
  hideCommerce?: boolean;
  hideDepartments?: boolean;
  showShippingNumber?: boolean;
}

const statusOptions = Object.entries(RETURNS_INTAKE_STATUS_LABELS).map(
  ([value, label]) => ({ value, label })
);

const SearchBar = ({
  search,
  initialFilter,
  filter,
  hideStatus = false,
  dateLabel,
  hideCommerce = false,
  hideDepartments = false,
  showShippingNumber = false,
}: SearchBarProps) => {
  const [localFilter, setLocalFilter] = useState<ReturnsIntakeFilters>(filter);

  useEffect(() => {
    setLocalFilter(filter);
  }, [filter]);

  const {
    selectedDriver,
    driverSearch,
    setDriverSearch,
    driverOptions,
    isDriversLoading,
    handleDriverSelect,
    clearDriver,
  } = useDriverSearchInput({ driverId: localFilter.driverId });

  const {
    resolvedSelectedCommerce,
    commerceSearch,
    setCommerceSearch,
    commerceOptions,
    isCommercesLoading,
    handleCommerceSelect,
    clearCommerce,
  } = useCommerceSearchInput({ commerceId: localFilter.commerceId });

  const {
    selectedDepartment,
    departmentSearch,
    setDepartmentSearch,
    departmentOptions,
    isDepartmentsLoading,
    handleDepartmentSelect,
    clearDepartment,
  } = useDepartmentSearchInput({
    departmentId: localFilter.departmentId,
    skip: hideDepartments,
  });

  const resetFilter = () => {
    setLocalFilter(initialFilter);
    clearDriver();
    clearCommerce();
    clearDepartment();
    search(initialFilter);
  };

  const handleSearch = () => {
    search(localFilter);
  };

  return (
    <div className='w-full flex flex-col justify-center gap-5 p-5 bg-white rounded-sm shadow-xl'>
      <h4 className='text-xl font-black text-gray-800'>Filtros de Búsqueda</h4>

      <div className={`grid grid-cols-1 md:grid-cols-3 gap-5`}>
        {showShippingNumber && (
          <TextInput
            name='shipping-number-search'
            placeholder='ID Pedido'
            value={localFilter.shippingNumber || ''}
            onChange={(val) =>
              setLocalFilter((prev) => ({
                ...prev,
                shippingNumber: val as string,
              }))
            }
          />
        )}
        <AutocompleteInput
          name='driver-search'
          placeholder='Conductor'
          value={selectedDriver}
          onChange={(option) => {
            handleDriverSelect(option);
            setLocalFilter((prev) => ({
              ...prev,
              driverId: (option?.data as string) || '',
            }));
          }}
          onInputChange={setDriverSearch}
          inputValue={driverSearch}
          options={driverOptions}
          required={false}
          loading={isDriversLoading}
        />
        {!hideCommerce && (
          <AutocompleteInput
            name='commerce-search'
            placeholder='Comercio'
            value={resolvedSelectedCommerce}
            onChange={(option) => {
              handleCommerceSelect(option);
              setLocalFilter((prev) => ({
                ...prev,
                commerceId: (option?.data as string) || '',
              }));
            }}
            onInputChange={setCommerceSearch}
            inputValue={commerceSearch}
            options={commerceOptions}
            required={false}
            loading={isCommercesLoading}
          />
        )}
        {!hideStatus && (
          <SelectInput
            name='returns-intake-status-search'
            placeholder='Estado Rendición'
            value={localFilter.returnsIntakeStatus || ''}
            onChange={(statusValue) =>
              setLocalFilter((prev) => ({
                ...prev,
                returnsIntakeStatus:
                  statusValue === ''
                    ? undefined
                    : (statusValue as 'OPEN' | 'CLOSED'),
              }))
            }
            options={statusOptions}
          />
        )}
        {!hideDepartments && (
          <AutocompleteInput
            name='department-search'
            placeholder='Departamento'
            value={selectedDepartment}
            onChange={(option) => {
              handleDepartmentSelect(option);
              setLocalFilter((prev) => ({
                ...prev,
                departmentId: (option?.data as string) || '',
              }));
            }}
            onInputChange={setDepartmentSearch}
            inputValue={departmentSearch}
            options={departmentOptions}
            required={false}
            loading={isDepartmentsLoading}
          />
        )}
        <DateInput
          name='start-date-search'
          placeholder={
            dateLabel
              ? `${dateLabel} desde`
              : hideStatus
                ? 'Deuda desde'
                : 'Fecha Rendición desde'
          }
          value={localFilter.startDate ?? null}
          onChange={(val) =>
            setLocalFilter((prev) => ({ ...prev, startDate: val }))
          }
        />
        <DateInput
          name='end-date-search'
          placeholder={
            dateLabel
              ? `${dateLabel} hasta`
              : hideStatus
                ? 'Deuda hasta'
                : 'Fecha Rendición hasta'
          }
          value={localFilter.endDate ?? null}
          onChange={(val) =>
            setLocalFilter((prev) => ({ ...prev, endDate: val }))
          }
        />
      </div>

      <div className='flex gap-3 justify-end'>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={resetFilter}
        >
          <TrashIcon color='primary' />
          Limpiar
        </GlobalButton>
        <GlobalButton
          variant={GlobalButtonVariant.primaryButton}
          action={handleSearch}
        >
          <SearchIcon sx={{ color: 'white' }} />
          Buscar
        </GlobalButton>
      </div>
    </div>
  );
};

export default SearchBar;
